import React, { Component } from 'react';
import { Route, Switch, withRouter, Redirect } from 'react-router-dom';
import PropTypes from 'prop-types';
import asyncComponent from '../../hoc/asyncComponent/asyncComponent';
import { connect } from 'react-redux';
import * as actions from '../../store/actions/index';
import { setBrandColors } from '../../shared/utility';

import Layout from '../../hoc/Layout/Layout';

import Home from '../Home/Home';
import Invoice from '../Invoice/Invoice';
import Test from '../Test/Test';

// Lazy load components, only import them if they are open by the user (code splitting, smaller bundle file size by splitting code into multiple files)
const asyncResults = asyncComponent(async () => await import('../Results/Results'));
const asyncItemDetails = asyncComponent(async () => await import('../Items/ItemDetails/ItemDetails'));
const asyncResetPassword = asyncComponent(async () => await import('../User/ResetPassword/ResetPassword'));
const asyncSetPassword = asyncComponent(async () => await import('../User/SetPassword/SetPassword'));
const asyncVerification = asyncComponent(async () => await import('../User/Verification/Verification'));
const asyncUserProfile = asyncComponent(async () => await import('../User/Profile/Profile'));
const asyncPaymentPreferences = asyncComponent(async () => await import('../PaymentPreferences/PaymentPreferences'));
const asyncAddDirectDebit = asyncComponent(async () => await import('../PaymentPreferences/AddDirectDebit/AddDirectDebit'));
const asyncCart = asyncComponent(async () => await import('../Cart/Cart'));
const asyncCheckoutMessage = asyncComponent(async () => await import('../Cart/CheckoutMessage/CheckoutMessage'));
const asyncReservations = asyncComponent(async () => await import('../Reservations/Reservations'));
const asyncReservationDetails = asyncComponent(async () => await import('../Reservations/ReservationDetails/ReservationDetails'));
const asyncOrders = asyncComponent(async () => await import('../Orders/Orders'));
const asyncOrderDetails = asyncComponent(async () => await import('../Orders/OrderDetails/OrderDetails'));

class App extends Component {
    /* * * * * * * * * * * * * *
     * REACT LIFECYCLE METHODS *
     * * * * * * * * * * * * * */
    componentDidMount() {
        if (!this.props.communityDetailsData && !this.props.communityDetailsLoading && !this.props.communityDetailsError) {
            this.props.onFetchCommunityDetails();
        }
        this.props.onCheckUserDataValidation();
        this.props.onSetLocalization(null);
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevProps.communityDetailsData !== this.props.communityDetailsData && !!this.props.communityDetailsData) {
            // set brand colors
            setBrandColors(
                this.props.communityDetailsData.applicationCustomization.primaryColor,
                this.props.communityDetailsData.applicationCustomization.secondaryColor
            );

            this.props.onFetchMainTypes();
            this.props.onFetchItemTypes();
        }
        if(prevProps.userRedirectPath !== this.props.userRedirectPath && !!this.props.userRedirectPath) {
            this.props.history.push({ pathname: `/${this.props.userRedirectPath}/` });
            this.props.onSetGlobalState('userRedirectPath', null);
        }
        if (!this.props.currentLocationData && !this.props.currentLocationLoading && !this.props.currentLocationError && this.props.currentLocationAvailable) {
            this.props.onFetchCurrentLocation();
        }

        if (this.props.isAuthorized) {
            if (prevProps.isAuthorized !== this.props.isAuthorized) {
                this.props.onSetGlobalState(['isLoginOpen','isSignupOpen','isMenuOpen'], false);
            }
            if (prevProps.userData !== this.props.userData) {
                this.props.onSetLocalization(this.props.userData.preferences.preferredLanguageCode);
            }
            if (!this.props.paymentAccountDetailsData && !this.props.paymentAccountDetailsLoading && !this.props.paymentAccountDetailsError) {
                this.props.onFetchPaymentAccountDetails(this.props.userData.preferences.preferredPaymentAccountId);
            } else if (prevProps.userData !== this.props.userData && !!prevProps.userData && !!prevProps.userData.preferences) {
                if (prevProps.userData.preferences.preferredPaymentAccountId !== this.props.userData.preferences.preferredPaymentAccountId) {
                    this.props.onFetchPaymentAccountDetails(this.props.userData.preferences.preferredPaymentAccountId);
                }
            }
            if (prevProps.paymentAccountDetailsData !== this.props.paymentAccountDetailsData && !!this.props.paymentAccountDetailsData) {
                this.props.onFetchCart(this.props.paymentAccountDetailsData.id);
            }
        }
    }

    /* * * * * *
     * RENDER  *
     * * * * * */
    render() {
        const routes = !this.props.isAuthorized ? (
            <Switch>
                <Route path="/items/:mainTypeId/" component={asyncResults} />
                <Route path="/item/:itemId/" component={asyncItemDetails} />
                <Route path="/reset-password/" component={asyncResetPassword} />
                <Route path="/set-password/" component={asyncSetPassword} />
                <Route path="/verification/" component={asyncVerification} />
                <Route path="/testcomponents/" component={Test} />
                <Route exact path="/" component={Home} />
                {/* <Route render={() => <h1>Not Found</h1>} /> */}
                <Redirect to="/" />
            </Switch>
        ) : (
            <Switch>
                <Route path="/items/:mainTypeId/" component={asyncResults} />
                <Route path="/item/:itemId/" component={asyncItemDetails} />
                <Route path="/profile/" component={asyncUserProfile} />
                <Route path="/payment-preferences/add/" component={asyncAddDirectDebit} />
                <Route exact path="/payment-preferences/" component={asyncPaymentPreferences} />
                <Route path="/cart/" component={asyncCart} />
                <Route path="/checkout/" component={asyncCheckoutMessage} />
                <Route exact path="/reservations/:reservationId/" component={asyncReservationDetails} />
                <Route exact path="/reservations/" component={asyncReservations} />
                <Route exact path="/orders/:orderId/" component={asyncOrderDetails} />
                <Route exact path="/orders/" component={asyncOrders} />
                <Route path="/invoice/:invoiceId/" component={Invoice} />
                <Route path="/testcomponents/" component={Test} />
                <Route exact path="/" component={Home} />
                {/* <Route render={() => <h1>Not Found</h1>} /> */}
                <Redirect to="/" />
            </Switch>
        );

        return this.props.locale && /\/invoice\/\S+$/.test(this.props.location.pathname) ? routes : this.props.locale ? <Layout>{routes}</Layout> : null;
    }
}

App.propTypes = {
    // properties
    location: PropTypes.object.isRequired,
    history: PropTypes.object.isRequired,

    isAuthorized: PropTypes.bool.isRequired,
    userData: PropTypes.object,
    userRedirectPath: PropTypes.string,
    communityDetailsData: PropTypes.object,
    communityDetailsLoading: PropTypes.bool,
    communityDetailsError: PropTypes.string,

    currentLocationAvailable: PropTypes.bool,
    currentLocationData: PropTypes.object,
    currentLocationLoading: PropTypes.bool,
    currentLocationError: PropTypes.string,

    paymentAccountDetailsData: PropTypes.object,
    paymentAccountDetailsLoading: PropTypes.bool,
    paymentAccountDetailsError: PropTypes.string,

    locale: PropTypes.string,

    // functions
    onCheckUserDataValidation: PropTypes.func.isRequired,
    onFetchCommunityDetails: PropTypes.func.isRequired,
    onFetchMainTypes: PropTypes.func.isRequired,
    onFetchItemTypes: PropTypes.func.isRequired,
    onFetchCurrentLocation: PropTypes.func.isRequired,
    onFetchPaymentAccountDetails: PropTypes.func.isRequired,
    onFetchCart: PropTypes.func.isRequired,
    onSetLocalization: PropTypes.func.isRequired,
    onSetGlobalState: PropTypes.func.isRequired
};

const mapStateToProps = state => {
    return {
        // user
        isAuthorized: !!state.details.user.data,
        userData: state.details.user.data,
        userRedirectPath: state.global.userRedirectPath,
        // community
        communityDetailsData: state.details.community.data,
        communityDetailsLoading: state.details.community.loading,
        communityDetailsError: state.details.community.error,
        // current location
        currentLocationData: state.details.currentLocation.data,
        currentLocationLoading: state.details.currentLocation.loading,
        currentLocationError: state.details.currentLocation.error,
        currentLocationAvailable: state.global.currentLocationAvailable,
        // payment accounts
        paymentAccountDetailsData: state.details.paymentAccount.data,
        paymentAccountDetailsLoading: state.details.paymentAccount.loading,
        paymentAccountDetailsError: state.details.paymentAccount.error,

        locale: state.global.localization.locale
    };
};

const mapDispatchToProps = dispatch => {
    return {
        onCheckUserDataValidation: () => dispatch(actions.checkUserDataValidation()),
        onFetchCommunityDetails: (subdomain) => dispatch(actions.fetchCommunityDetails(subdomain)),
        onFetchMainTypes: () => dispatch(actions.fetchMainTypes()),
        onFetchItemTypes: () => dispatch(actions.fetchItemTypes()),
        onFetchCurrentLocation: () => dispatch(actions.fetchCurrentLocation()),
        onFetchPaymentAccountDetails: (accountId) => dispatch(actions.fetchPaymentAccountDetails(accountId)),
        onFetchCart: (paymentId) => dispatch(actions.fetchCart(paymentId)),
        onSetLocalization: (locale) => dispatch(actions.setLocalization(locale)),
        onSetGlobalState: (identifier, value) => dispatch(actions.setGlobalState(identifier, value))
    };
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(App));



// WEBPACK FOOTER //
// ./src/containers/App/App.js